/*global cordial, CordialTrack */
import _ from 'underscore';
import { all, call, fork, select, put, take } from 'redux-saga/effects';

export function* setupCordial() {
    const key = yield select(state => state.cordial && state.cordial.key);

    yield call(() => new Promise((resolve) => {
        const tag = document.createElement('script');
        tag.async = false;
        tag.onload = resolve;
        tag.setAttribute('data-auto-track', false);
        tag.setAttribute('data-cordial-track-key', key);
        tag.setAttribute('data-base-domain', 'tarot.com');
        tag.setAttribute('data-cordial-url', 'd.cordial-tracking.tarot.com');
        tag.src = 'https://d.cordial-tracking.tarot.com/track.js';
        document.body.appendChild(tag);
    }));
}

export function* getBrowseEvent() {
    const [ desc, teeup ] = yield all([
        select(state => state.product.description),
        select(state => state.product.teeup),
    ]);

    if (desc) {
        return {
            category: desc.categories[0],
            product: desc.product_name,
            productID: desc.code,
            placement: 'teeup',
        };
    } else if (teeup) {
        return {
            category: teeup.product_type,
            product: teeup.display_name,
            productID: teeup.code,
            placement: 'module',
        };
    }

    return null;
}

export function* sendSavedBrowseEvents() {
    const events = yield select(state => state.cookie['cordial-events'] || []);
    const impersonate = yield select(state => state.cookie.impersonate || null);

    if (!impersonate) {
        yield put({ type: 'cookie:remove', payload: 'cordial-events' });

        yield all(_.last(events, 5).map((event) =>
            call([cordial, cordial.event], 'browse', event)
        ));
    }
}

export function* saveBrowseEvent(event) {
    const events = yield select(state => state.cookie['cordial-events'] || []);
    events.push(event);
    yield put({ type: 'cookie:set', payload: {
        name: 'cordial-events',
        value: events,
        opts: { expires: .042 }, // .042 = about 1 hour
    }});
}

export function* sendBrowseEvent(event) {
    const impersonate = yield select(state => state.cookie.impersonate || null);
    try {
        if (!impersonate) {
            yield call([cordial, cordial.event], 'browse', event);
        }
    } catch(e) {
        yield put({ type: 'raven:error', payload: {error: e, message: 'sendBrowseEvent'} });
    }
}

export function* sendBrowsePageEvent(event) {
    const impersonate = yield select(state => state.cookie.impersonate || null);
    try {
        if (!impersonate) {
            yield call([cordial, cordial.event], 'browse-page', event);
        }
    } catch(e) {
        yield put({ type: 'raven:error', payload: {error: e, message: 'sendBrowseEvent'} });
    }
}

export function* watchUser() {
    while (true) {
        const user = yield select(state => state.user);
        if (user.member_uuid) {
            try {
                yield call(() => new Promise((resolve, reject) =>
                    cordial.identify(user.member_uuid, {
                        knownContactCallback: resolve,
                        unknownContactCallback: () => reject(new Error('identifyUserFailure')),
                    })
                ));
            } catch(e) {
                try {
                    yield call([cordial, cordial.contact], {m_uuid: user.member_uuid}, {upsert: true});
                } catch(f) {
                    yield put({ type: 'raven:error', payload: {error: f, message: 'cordial.contact'} });
                }
            }
            try {
                yield call(sendSavedBrowseEvents);
            } catch(e) {
                yield put({ type: 'raven:error', payload: {error: e, message: 'sendSavedBrowseEvents'} });
            }
        } else {
            try {
                yield call([cordial, cordial.forget]);
            } catch(e) {
                yield put({ type: 'raven:error', payload: {error: e, message: 'cordial.forget'} });
            }
        }

        yield take(['user:logged-in', 'user:logged-out']);
    }
}

export function* watchBrowseEvents() {
    while (true) {
        const [event, cordial_cid] = yield all([
            call(getBrowseEvent),
            call([CordialTrack, CordialTrack.getCordialCookies], 'cID'),
        ]);

        if (event && cordial_cid) {
            yield call(sendBrowseEvent, event);
        } else {
            yield call(saveBrowseEvent, event);
        }

        yield take('cordial:browse');
    }
}

export function* watchBrowsePage() {
    while (true) {
        const tags = yield select(state => state.cordial && state.cordial.tags);

        if (tags) {
            const event = {
                url: document.location.href,
                title: document.title,
                tags: tags,
            };

            yield call(sendBrowsePageEvent, event);
        }

        yield take('cordial:page');
    }
}

export function* root() {
    // NOTE: we're hardcoding the fact that the only supported events are browse events.
    yield take('cordial:init');

    yield call(setupCordial);
    yield call([CordialTrack, CordialTrack.setCordialCookies], 'cID', '', {expires: 0});

    yield all([
        fork(watchUser),
        fork(watchBrowseEvents),
        fork(watchBrowsePage),
    ]);
}
